import dayjs from 'dayjs';
import {z} from 'zod';

import i18n from '@/i18n';
import {graphqlClient} from '@/services/graphqlClient';
import type {ResidentMedicationExportFragmentFragment} from '@/services/graphqlHooks';
import {
  useGetResidentAllMedicationsForExportQuery,
  useGetResidentCurrentMedicationsForExportQuery,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';
import medicationFoodRelation from '@/translation/medicationFoodRelation';
import medicationForm from '@/translation/medicationForm';
import treatmentType from '@/translation/treatmentType';
import unitType from '@/translation/unitType';
import {TIME_FORMATS} from '@/utils/timeFormats';
import {formatWeekDaysShort} from '@/utils/weekDays';

import MedicationsOptionsFields from '../../components/export-options/MedicationsOptionsFields';
import type {ExportDefinition} from '../../types/exportTypes';
import {
  formatExportDate,
  getResidentDefaultOptions,
  residentOptionsSchema,
} from '../exportOptions';
import {EXPORT_MEDICATION_SCOPE_PARAM} from '../exportParams';

export const MEDICATION_SCOPES = ['current', 'all'] as const;

export type MedicationScope = (typeof MEDICATION_SCOPES)[number];

export const medicationScopes: Record<MedicationScope, string> = {
  current: i18n.t('Current medications'),
  all: i18n.t('All medications'),
};

const medicationsOptionsSchema = residentOptionsSchema.extend({
  scope: z.enum(MEDICATION_SCOPES),
});

export type MedicationsOptions = z.infer<typeof medicationsOptionsSchema>;

const medicationLoaders: Record<
  MedicationScope,
  (residentId: string) => Promise<ResidentMedicationExportFragmentFragment[]>
> = {
  current: async residentId => {
    const variables = {
      input: {residentId, date: dayjs().format(TIME_FORMATS.awsDate)},
    };
    const {getResidentCurrentMedications} = await queryClient.fetchQuery({
      queryKey:
        useGetResidentCurrentMedicationsForExportQuery.getKey(variables),
      queryFn: useGetResidentCurrentMedicationsForExportQuery.fetcher(
        graphqlClient,
        variables
      ),
    });

    return getResidentCurrentMedications;
  },
  all: async residentId => {
    const variables = {input: {residentId}};
    const {getResidentAllMedications} = await queryClient.fetchQuery({
      queryKey: useGetResidentAllMedicationsForExportQuery.getKey(variables),
      queryFn: useGetResidentAllMedicationsForExportQuery.fetcher(
        graphqlClient,
        variables
      ),
    });

    return getResidentAllMedications;
  },
};

const isMedicationScope = (value: string | null): value is MedicationScope =>
  MEDICATION_SCOPES.some(scope => scope === value);

const formatDosageDetails = (
  details: ResidentMedicationExportFragmentFragment['schedule']['dosageDetails'][number]
) =>
  details &&
  [
    `${details.dosage.quantity} ${unitType[details.dosage.unit]}`,
    details.time,
    details.relationToFood && medicationFoodRelation[details.relationToFood],
    details.customInstructions,
  ]
    .filter(Boolean)
    .join(' ');

export const medicationsExport: ExportDefinition<
  MedicationsOptions,
  ResidentMedicationExportFragmentFragment
> = {
  id: 'medications',
  label: i18n.t('Resident medications'),
  permission: 'EXPORT_MEDICATION',
  entitlement: 'MEDICATION',
  optionsSchema: medicationsOptionsSchema,
  OptionsFields: MedicationsOptionsFields,
  getDefaultOptions: searchParams => {
    const scope = searchParams.get(EXPORT_MEDICATION_SCOPE_PARAM);

    return {
      ...getResidentDefaultOptions(searchParams),
      scope: isMedicationScope(scope) ? scope : 'current',
    };
  },
  load: async ({residentId, scope}) => {
    const medications = await medicationLoaders[scope](residentId);

    return [...medications].sort((medicationA, medicationB) =>
      dayjs(medicationB.startDate).diff(medicationA.startDate)
    );
  },
  describeOptions: ({scope}) => medicationScopes[scope],
  columns: [
    {
      header: i18n.t('Medication name'),
      value: medication => medication.medicationName,
    },
    {
      header: i18n.t('Form'),
      filterable: true,
      value: medication => medication.form && medicationForm[medication.form],
    },
    {
      header: i18n.t('Treatment type'),
      filterable: true,
      value: medication =>
        medication.treatmentType && treatmentType[medication.treatmentType],
    },
    {
      header: i18n.t('Treatment start date'),
      value: medication => formatExportDate(medication.startDate),
    },
    {
      header: i18n.t('Treatment end date'),
      value: medication => formatExportDate(medication.endDate),
    },
    {
      header: i18n.t('Days'),
      value: ({schedule}) =>
        schedule.daysOfWeek?.length && schedule.daysOfWeek.length < 7
          ? formatWeekDaysShort(schedule.daysOfWeek)
          : i18n.t('Daily'),
    },
    {
      header: i18n.t('Dosage'),
      value: ({schedule}) =>
        schedule.dosageDetails
          .map(formatDosageDetails)
          .filter(Boolean)
          .join(', '),
    },
    {header: i18n.t('Notes'), value: medication => medication.notes},
    {
      header: i18n.t('Warnings'),
      defaultHidden: true,
      value: medication => medication.warnings,
    },
  ],
  getFileName: ({scope}) =>
    scope === 'current' ? 'medications' : 'medications-all',
};
